import {useState, useEffect, useRef, useCallback} from 'react';
import {Link, useLocation} from 'react-router-dom';

import {GrAppsRounded} from 'react-icons/gr';
import {TbPhotoHeart} from 'react-icons/tb';

import {useTheme} from '../Utils/ThemeContext';
import ScrollWatcher from '../Components/ScrollWatcher';

import {NavItem} from '../declarations';

import LogoLight from '../assets/svg/font_light.svg';
import LogoDark from '../assets/svg/font_dark.svg';

/* -------------------- Navigation Configuration -------------------- */
const NAV_ITEMS: NavItem[] = [
    {path: '/digital', label: 'Digital', icon: GrAppsRounded},
    {path: '/print', label: 'Print', icon: TbPhotoHeart},
];

/* -------------------- Navigation Style Builder -------------------- */
const navCls = (active: boolean, mobile = false) =>
    [
        'inline-flex items-center gap-2 font-medium text-sm rounded-lg border transition-all overflow-hidden',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))]',
        mobile ? 'px-4 py-3 w-full' : 'px-4 py-2',
        active
            ? 'text-[rgb(var(--accent-primary))] border-[rgb(var(--accent-primary))] bg-[rgb(var(--accent-primary))]/8'
            : 'text-[rgb(var(--text-primary))] border-[rgb(var(--border))] bg-[rgb(var(--bg-card))] hover:border-[rgb(var(--accent-primary))] hover:bg-[rgb(var(--accent-primary))]/5',
    ].join(' ');

/* -------------------- Constants -------------------- */
const HEADER_HEIGHT = 68;
const HEADER_HEIGHT_MOBILE = 60;
const SCROLL_SPEED_FACTOR = 0.25;

/* -------------------- Header -------------------- */
const Header = () => {
    const {pathname} = useLocation();
    const {theme} = useTheme();
    const [open, setOpen] = useState(false);

    /*
     * Smooth Scroll State
     * headerOffset: 0 = fully visible, -headerHeight = fully hidden
     */
    const [headerOffset, setHeaderOffset] = useState(0);
    const lastScrollY = useRef(0);
    const accumulatedScroll = useRef(0);
    const ticking = useRef(false);

    const menuRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLLabelElement>(null);

    const logo = theme === 'digital' ? LogoLight : LogoDark;

    /* -------------------- Menu Controls -------------------- */
    const toggleMenu = () => setOpen((v) => !v);
    const closeMenu = () => setOpen(false);

    /* -------------------- Auto Close on Route Change -------------------- */
    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    /* -------------------- Close on ESC Key -------------------- */
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeMenu();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open]);

    /* -------------------- Close on Outside Click -------------------- */
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                menuRef.current?.contains(target) ||
                btnRef.current?.contains(target)
            )
                return;
            closeMenu();
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);

    /* -------------------- Smooth Scroll Tracking -------------------- */
    const updateHeaderOffset = useCallback(() => {
        const currentScrollY = window.scrollY;
        const headerHeight =
            window.innerWidth >= 640 ? HEADER_HEIGHT : HEADER_HEIGHT_MOBILE;
        const scrollDelta = currentScrollY - lastScrollY.current;

        /* Wenn ganz oben: immer voll sichtbar */
        if (currentScrollY <= 0) {
            accumulatedScroll.current = 0;
            setHeaderOffset(0);
            lastScrollY.current = currentScrollY;
            return;
        }

        /*
         * Kumulierte Scroll-Distanz mit Geschwindigkeitsfaktor:
         * SCROLL_SPEED_FACTOR = 0.5 bedeutet, der Header bewegt sich nur halb so schnell
         * wie der tatsächliche Scroll. Das macht die Animation langsamer und smoother.
         */
        if (scrollDelta > 0) {
            /* Runterscrollen: Header verstecken (langsamer) */
            accumulatedScroll.current = Math.min(
                accumulatedScroll.current + scrollDelta * SCROLL_SPEED_FACTOR,
                headerHeight,
            );
        } else if (scrollDelta < 0) {
            /* Hochscrollen: Header zeigen (langsamer) */
            accumulatedScroll.current = Math.max(
                accumulatedScroll.current + scrollDelta * SCROLL_SPEED_FACTOR,
                0,
            );
        }

        /* Offset berechnen und setzen (negativ = nach oben verschoben) */
        const offset = -Math.min(accumulatedScroll.current, headerHeight);
        setHeaderOffset(offset);

        lastScrollY.current = currentScrollY;
    }, []);

    useEffect(() => {
        const onScroll = () => {
            if (!ticking.current) {
                window.requestAnimationFrame(() => {
                    updateHeaderOffset();
                    ticking.current = false;
                });
                ticking.current = true;
            }
        };

        window.addEventListener('scroll', onScroll, {passive: true});
        return () => window.removeEventListener('scroll', onScroll);
    }, [updateHeaderOffset]);

    return (
        <>
            {/* Scroll Progress Watcher */}
            <div className='fixed top-0 left-0 right-0 z-[9999]'>
                <ScrollWatcher />
            </div>

            {/* Header - gleitet smooth und langsamer mit dem Scrollen */}
            <header
                className={`fixed top-0 left-0 right-0 z-50 bg-[rgb(var(--bg-secondary))]/60 backdrop-blur-md border-b border-[rgb(var(--border))]/40 shadow-sm will-change-transform`}
                style={{
                    transform: `translateY(${headerOffset}px)`,
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <div className='page-container'>
                    <div className='flex items-center justify-between h-[60px] sm:h-[68px]'>
                        {/* Logo */}
                        <Link
                            to='/'
                            onClick={closeMenu}
                            className='flex items-center p-1'
                        >
                            <img
                                src={logo}
                                alt='es2be Home'
                                className='h-9 sm:h-11'
                            />
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className='hidden md:flex gap-6'>
                            {NAV_ITEMS.map(({path, label, icon: Icon}) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className={navCls(pathname === path)}
                                >
                                    <Icon size={17} className='flex-shrink-0' />
                                    <span className='truncate'>{label}</span>
                                </Link>
                            ))}
                        </nav>

                        {/* Mobile Menu Toggle - mit mehr Abstand zum rechten Rand */}
                        <div className='md:hidden pr-1'>
                            <input
                                id='check-icon'
                                hidden
                                className='check-icon'
                                type='checkbox'
                                checked={open}
                                onChange={toggleMenu}
                            />
                            <label
                                ref={btnRef}
                                className='icon-menu cursor-pointer flex flex-col mr-2'
                                htmlFor='check-icon'
                                aria-expanded={open}
                                aria-label={open ? 'Close menu' : 'Open menu'}
                            >
                                <div
                                    className={`bar bar--1 ${open ? 'open' : ''}`}
                                />
                                <div
                                    className={`bar bar--2 ${open ? 'open' : ''}`}
                                />
                                <div
                                    className={`bar bar--3 ${open ? 'open' : ''}`}
                                />
                            </label>
                        </div>
                    </div>
                </div>
            </header>

            {/* Layout Spacer */}
            <div className='h-[60px] sm:h-[68px]' />

            {/* Mobile Navigation */}
            <div
                ref={menuRef}
                className={[
                    'fixed left-0 right-0 md:hidden z-40',
                    'top-[60px] sm:top-[68px]',
                    'bg-[rgba(255,255,255,0.06)] backdrop-blur-xl border-b border-white/10 overflow-hidden',
                    'before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:opacity-40',
                    'transform transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    open
                        ? 'translate-y-0 opacity-100 pointer-events-auto'
                        : '-translate-y-full opacity-0 pointer-events-none',
                ].join(' ')}
            >
                <div className='relative px-4 py-4 flex flex-col gap-3'>
                    {NAV_ITEMS.map(({path, label, icon: Icon}) => (
                        <Link
                            key={path}
                            to={path}
                            onClick={closeMenu}
                            className={navCls(pathname === path, true)}
                        >
                            <Icon size={17} className='flex-shrink-0' />
                            <span className='truncate'>{label}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </>
    );
};

export default Header;
